import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterService } from '../../../../services/router/router.service';
import { APP_ROUTES } from '../../../../utils/route.constant';
import { DataService } from '../../../../services/data/data.service';
import { IContractEnrollmentResponse } from '../../../../interfaces/IEnrollment';
import { IContractData, IPaymentInfo } from '../../../../interfaces/IJob';
import { IBaseCoverageResponse } from '../../../../interfaces/IEnrollment';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { getMonthlyCost, getTaxAmount, getTotalMonthlyCost } from '../special-member-offer-v2.helper';
import { ILinkPlanReqBody } from '../../../../interfaces/ISearchPlan';
import { ILinkPlan } from '../../../../interfaces/ISearchContract';
import { SearchPlanService } from '../../../../services/searchPlan/searchPlanService.service';
import { UtilitiesService } from '../../../../services/utilities/utilities.service';
import { SPECIAL_MEMBER_OFFER_CONTENT } from '../special-member-offer-v2.config';
import { ICrossSellContent } from '../../../../interfaces/ICrossSellContent';
import { Ga4Service } from '../../../../services/ga4/ga4.service';
import { CE_ENROLLMENT_PRINT, CE_PLAN_LINK_FAILURE, CE_PLAN_LINK_SUCCESS } from '../../../../services/ga4/ga4.constant';

@Component({
  selector: 'app-enroll-success',
  templateUrl: './enroll-success.component.html',
  styleUrls: ['./enroll-success.component.scss']
})
export class EnrollSuccessComponent implements OnInit, OnDestroy {
  enrollmentInfo: IContractEnrollmentResponse | null = null;
  contractData: IContractData | null = null;
  productDetails: IBaseCoverageResponse | null = null;
  cleaner$ = new Subject<void>();
  emailAddress: string = '';
  billingData: IPaymentInfo | null = null;
  paymentPreference: string | null = null;
  monthlyCost: number | null = null;
  taxAmount: number | null = null;
  totalMonthlyCost: number | null = null;
  currentDate = new Date();
  linkingPlan = false;
  errorMessage: string | null = null;
  content = SPECIAL_MEMBER_OFFER_CONTENT;
  linkError: string | null = null;
  contentToBeDisplayed: ICrossSellContent | null = null;

  constructor(
    private routerService: RouterService,
    private data: DataService,
    private searchPlanService: SearchPlanService,
    private util: UtilitiesService,
    private gaService: Ga4Service
  ) { }

  ngOnInit(): void {
    combineLatest({
      contractData: this.data.contractData$,
      productInfo: this.data.productInfo$,
      enrollmentInfo: this.data.enrollmentInfo$,
      crossSellContent: this.data.crossSellContent$,
      enrollmentPaymentInfo: this.data.enrollmentPaymentInfo$
    }).pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: ({ contractData, productInfo, enrollmentInfo, crossSellContent, enrollmentPaymentInfo }) => {
          this.contractData = contractData;
          this.productDetails = productInfo;
          this.enrollmentInfo = enrollmentInfo;
          this.contentToBeDisplayed = crossSellContent;
          this.linkPlanToExistingUser(this.enrollmentInfo);

          const paymentInfo = enrollmentPaymentInfo || contractData?.paymentInfo || null;

          if (contractData || enrollmentPaymentInfo) {
            this.emailAddress = contractData?.baseContract?.primaryCustomerEmail || contractData?.contact?.emailAddress || '';
            this.billingData = paymentInfo;
            this.paymentPreference = paymentInfo?.billMechCode ?? null;
          }

          if (productInfo) {
            this.monthlyCost = getMonthlyCost(productInfo?.productInfo?.cost) ?? null;
            this.taxAmount = getTaxAmount(productInfo?.taxRate, this.monthlyCost) ?? null;
            this.totalMonthlyCost = getTotalMonthlyCost(this.monthlyCost, this.taxAmount) ?? null;
          }
        }
      });
  }

  linkPlanToExistingUser(response: IContractEnrollmentResponse | null) {
    if (!response?.contractNumber) {
      this.routerService.goto(APP_ROUTES.DASHBOARD);
      return;
    }

    this.linkingPlan = true;
    let user = this.data.user$.getValue();
    const linkPlanReqObj: ILinkPlanReqBody = {
      cognitoSub: this.data.auth0UserId.getValue(),
      contractNumber: response.contractNumber,
      defaultFlag: 'N',
      email: user?.email || ''
    }
    this.searchPlanService.linkPlanToExistingUser(linkPlanReqObj)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (isPlanLinked: ILinkPlan) => {
          this.linkingPlan = false;
          if (isPlanLinked.successFlag === 'Y') {
            this.gaService.setGa4DtcSearsLanding(CE_PLAN_LINK_SUCCESS, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
            this.data.isPlanLinked$.next({ linked: true, contractNumber: response?.contractNumber || null });
          }
        },
        error: (err) => {
          this.linkingPlan = false;
          if (err.status === 409) {
            // contract is already linked, navigate to dashboard
            this.routerService.goto(APP_ROUTES.DASHBOARD);
            this.data.isPlanLinked$.next({ linked: true, contractNumber: response.contractNumber || null });
          } else {
            this.gaService.setGa4DtcSearsLanding(CE_PLAN_LINK_FAILURE, this.contractData?.baseContract?.clientId ?? 'Unknown Client', true);
            this.linkError = `We couldn't link your plan to your account. If you don't see your contract in the next 24 hours, please call us at ${this.contentToBeDisplayed?.contact || ''}`;
          }
        }
      })
  }

  printPage(): void {
    this.gaService.setGa4DtcSearsLanding(CE_ENROLLMENT_PRINT, this.contractData?.baseContract?.clientId ?? 'Unknown Client');
    window.print();
  }

  navigateToDashboard(): void {
    this.routerService.goto(APP_ROUTES.DASHBOARD);
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
    // clear all the enrollment data as this is the last step in the flow
    this.data.enrollmentInfo$.next(null);
    this.data.productInfo$.next(null);
    this.data.contractData$.next(null);
    this.data.enrollmentPaymentInfo$.next(null);
  }
}
